import type {
  AccountAdvanceLimits,
  AccountPreapproval,
  Personnel,
  PreapprovalApplicationStatus,
  PrincipalOwner,
} from "@/lib/brokerAccounts";
import type { AuthedRequester } from "@/lib/brokerAuth";
import { formatPreapprovalMoney } from "@/lib/bondValueDisplay";
import { formatShortDate } from "@/lib/listFormat";
import {
  advanceEvidenceStateLabel,
  ADVANCE_EVIDENCE_REQUIREMENTS,
  type AdvanceEvidence,
  type AdvanceEvidenceRequirement,
  type ReadAdvanceRequirements,
} from "@/lib/signalAdvanceEvidence";

// BRK-S407 — the Surety program tab's SIGNAL ADVANCE APPLICATION section
// (`#advance-app`): its pill, the "Steps to enroll" checklist and the client
// link rows.
//
// EVERY STATE IS SERVED. The pill reads the program type, the application and
// the evidence; each step reads one served fact and maps it to words — the
// credit check from the program in force (or the open application), bank and
// accounting from `advanceRequirements.{bank,accounting}.state` (uploaded
// documents count: PMS reports a met requirement `satisfied` however it was
// met), the underwriter review from `advanceRequirements.satisfied` (the
// application is with the underwriter) or an Advance program in force. Nothing
// here re-derives PMS's rule.
//
// NO FIGURE OF THE SPA'S OWN. The wireframe's "Earn up to $3,000,000 /
// $6,000,000" tiles are the program record's `advanceLimits` (BRK-S501,
// PMS-S915); null until Signal saves its program terms, when each tile reads
// "—", never $0. Once the insured holds Advance the section shows their own
// Advance `terms` instead.
//
// D4 (accepted 2026-10-24): the links may be EMAILED to the client. PMS-S840
// adds the connections read, the mint and the send, pinned since BRK-S511.

export type AdvanceStage = "recommended" | "in_review" | "enrolled";
export type AdvanceStepKey =
  | "credit"
  | AdvanceEvidenceRequirement
  | "underwriter";
export type AdvanceStepTone = "done" | "waiting" | "todo";

export interface AdvanceStep {
  key: AdvanceStepKey;
  label: string;
  sub: string;
  tone: AdvanceStepTone;
  /** The served state in words — null when PMS published nothing to read. */
  status: string | null;
}

export interface AdvanceLimit {
  label: string;
  value: string;
}

export interface AdvanceApplicationView {
  stage: AdvanceStage;
  title: string;
  pill: string;
  steps: AdvanceStep[];
  /** The insured's own Advance limits — empty unless they hold Advance. */
  limits: AdvanceLimit[];
  /** The program record's "Earn up to" limits — while not on Advance. */
  offer: AdvanceLimit[];
  /** Whether the client link rows offer anything: an open application whose
   *  evidence PMS publishes. */
  linksOpen: boolean;
}

/** An "up to" figure, or the dash that says it is not set (D-10: never $0). */
function offerFigure(value: number | undefined): string {
  return value === undefined ? "—" : formatPreapprovalMoney(value);
}

export interface AdvanceApplicationInput {
  preapproval: AccountPreapproval;
  /** The program record's Advance limits (PMS-S915); null while unset. */
  advanceLimits: AccountAdvanceLimits | null;
  application: { status: PreapprovalApplicationStatus } | null;
  advance: ReadAdvanceRequirements | null;
}

const STEP_COPY: Record<AdvanceStepKey, { label: string; sub: string }> = {
  credit: { label: "Credit check", sub: "On the business principal" },
  bank: { label: "Connect bank account", sub: "Through Plaid" },
  // The wireframe says "Through Rutter"; the accounting vendor is never
  // named to a broker (BRK-S322, vendorBranding.test.ts).
  accounting: {
    label: "Connect accounting system",
    sub: "Through your accounting software",
  },
  // The wireframe's "Underwriter review", in the portal's word (BRK-S501).
  underwriter: {
    label: "Signal review",
    sub: "Confirms the program rate",
  },
};

function stepOf(
  key: AdvanceStepKey,
  tone: AdvanceStepTone,
  status: string | null,
): AdvanceStep {
  return { key, ...STEP_COPY[key], tone, status };
}

/** One requirement's step, from its served state. */
function evidenceStep(
  key: AdvanceEvidenceRequirement,
  evidence: AdvanceEvidence | null,
): AdvanceStep {
  if (evidence === null) return stepOf(key, "todo", null);
  switch (evidence.state) {
    case "satisfied":
      return stepOf(
        key,
        "done",
        evidence.via === "documents" ? "Documents on file" : "Connected",
      );
    case "connected":
      return stepOf(key, "done", "Connected");
    case "link_sent":
    case "documents_received":
      return stepOf(key, "waiting", advanceEvidenceStateLabel(evidence.state));
    case "not_started":
      return stepOf(key, "todo", "Not connected");
    default:
      return stepOf(key, "todo", advanceEvidenceStateLabel(null));
  }
}

function creditStep(input: AdvanceApplicationInput): AdvanceStep {
  if (input.preapproval.status === "approved") {
    return stepOf("credit", "done", "Done");
  }
  const open = input.application?.status;
  return open === "running" || open === "under_review"
    ? stepOf("credit", "waiting", "In progress")
    : stepOf("credit", "todo", "Not started");
}

export function advanceApplication(
  input: AdvanceApplicationInput,
): AdvanceApplicationView {
  const { preapproval, advance } = input;
  const onAdvance =
    preapproval.status === "approved" && preapproval.programType === "advance";

  if (onAdvance) {
    const terms = preapproval.terms;
    return {
      stage: "enrolled",
      title: "Signal Advance",
      pill: "Enrolled",
      // The program in force is the served fact that every step is behind it.
      steps: [
        stepOf("credit", "done", "Done"),
        stepOf("bank", "done", "Done"),
        stepOf("accounting", "done", "Done"),
        stepOf("underwriter", "done", "Rate confirmed"),
      ],
      limits: terms
        ? [
            {
              label: "Per bond limit",
              value: formatPreapprovalMoney(terms.singleBondLimit),
            },
            {
              label: "Aggregate bonding capacity",
              value: formatPreapprovalMoney(terms.aggregateProgramLimit),
            },
          ]
        : [],
      offer: [],
      linksOpen: false,
    };
  }

  const inReview = advance?.satisfied === true;
  return {
    stage: inReview ? "in_review" : "recommended",
    title: "Signal Advance application",
    pill: inReview ? "In review" : "Recommended",
    steps: [
      creditStep(input),
      evidenceStep("bank", advance?.bank ?? null),
      evidenceStep("accounting", advance?.accounting ?? null),
      inReview
        ? stepOf("underwriter", "waiting", "In progress")
        : stepOf("underwriter", "todo", "After accounts connect"),
    ],
    limits: [],
    offer: [
      {
        label: "Per bond limit",
        value: offerFigure(input.advanceLimits?.singleBondLimit),
      },
      {
        label: "Aggregate bonding capacity",
        value: offerFigure(input.advanceLimits?.aggregateProgramLimit),
      },
    ],
    linksOpen: !inReview && advance !== null,
  };
}

// ── The client link rows (BRK-S407, BRK-S446) ──────────────────────────────
//
// Each link's facts come from PMS-S840's CONNECTIONS read, not the account:
//   GET  /api/broker/accounts/{id}/connections            → {connections: [...]}
//   POST /api/broker/accounts/{id}/connections/{kind}/request  (mint / re-mint)
//   POST /api/broker/accounts/{id}/connections/{kind}/send     {email}
// All three pinned since BRK-S511 (PMS PR #752), mirrored as
// `CONNECTION_MIRROR` in `test/contract/bondSeamMirror.ts`. `kind` is PMS's
// own `bank|accounting` — no vendor name. A link that lapsed unused reads
// `requested` with a past `expiresAt`; request a new one.

/** `connections[].status` (PMS-S840). */
export const CONNECTION_STATUSES = [
  "not_connected",
  "requested",
  "connected",
] as const;
export type ConnectionStatus = (typeof CONNECTION_STATUSES)[number];

/** One client connection (PMS-S840) — every field always present. */
export interface AccountConnection {
  kind: AdvanceEvidenceRequirement;
  status: ConnectionStatus;
  /** The client's connect link, once one is minted; null before. */
  linkUrl: string | null;
  /** When Signal last emailed it. */
  sentAt: string | null;
  /** When the link stops working; null when it has no expiry. */
  expiresAt: string | null;
  connectedAt: string | null;
}

const CONNECTIONS = (accountId: string) =>
  `/api/broker/accounts/${encodeURIComponent(accountId)}/connections`;

export async function listAccountConnections(
  request: AuthedRequester,
  accountId: string,
): Promise<AccountConnection[]> {
  const body = await request<{ connections: AccountConnection[] }>(
    CONNECTIONS(accountId),
  );
  return body.connections;
}

/** Mint the link, or a new one in place of an expired one. Confirmed only by
 *  PMS's answer, which is the connection as it now stands. */
export function requestAccountConnection(
  request: AuthedRequester,
  accountId: string,
  kind: AdvanceEvidenceRequirement,
): Promise<AccountConnection> {
  return request<AccountConnection>(
    `${CONNECTIONS(accountId)}/${kind}/request`,
    { method: "POST" },
  );
}

/** The connections by kind; a kind PMS did not list is null. */
export function connectionsByKind(
  list: readonly AccountConnection[] | null,
): Record<AdvanceEvidenceRequirement, AccountConnection | null> {
  const of = (kind: AdvanceEvidenceRequirement) =>
    list?.find((c) => c.kind === kind) ?? null;
  return { bank: of("bank"), accounting: of("accounting") };
}

export interface AdvanceLinkRow {
  status: string;
  /** "No link created yet", "Sent m/d/yyyy · expires m/d/yyyy" — served
   *  dates only. */
  detail: string | null;
  connected: boolean;
  /** Copy and Send are offered. */
  canAct: boolean;
  /** The served link has expired: offer "Create a new link" instead. */
  expired: boolean;
  sendLabel: "Send link" | "Resend";
}

/** One client link's row: its status line and what it offers. `now` decides
 *  only whether a SERVED expiry has passed. */
export function advanceLinkRow(
  evidence: AdvanceEvidence,
  connection: AccountConnection | null,
  now: Date,
): AdvanceLinkRow {
  const base = { expired: false, sendLabel: "Send link" as const };
  if (evidence.state === "satisfied" && evidence.via === "documents") {
    return {
      ...base,
      status: "Documents on file",
      detail: null,
      connected: true,
      canAct: false,
    };
  }
  if (
    connection?.status === "connected" ||
    evidence.state === "connected" ||
    evidence.state === "satisfied"
  ) {
    return {
      ...base,
      status: "Connected",
      detail: connection?.connectedAt
        ? `Connected ${formatShortDate(connection.connectedAt)}`
        : null,
      connected: true,
      canAct: false,
    };
  }
  if (connection === null) {
    return {
      ...base,
      status: advanceEvidenceStateLabel(null),
      detail: null,
      connected: false,
      canAct: false,
    };
  }
  const expiry = connection.expiresAt ? new Date(connection.expiresAt) : null;
  if (expiry && !Number.isNaN(expiry.getTime()) && expiry <= now) {
    return {
      ...base,
      status: "Expired",
      detail: `Expired ${formatShortDate(connection.expiresAt ?? "")}`,
      connected: false,
      canAct: false,
      expired: true,
    };
  }
  if (connection.sentAt) {
    return {
      status: "Sent",
      detail: [
        `Sent ${formatShortDate(connection.sentAt)}`,
        ...(connection.expiresAt
          ? [`expires ${formatShortDate(connection.expiresAt)}`]
          : []),
      ].join(" · "),
      connected: false,
      canAct: true,
      expired: false,
      sendLabel: "Resend",
    };
  }
  return {
    ...base,
    status: "Not sent",
    detail: connection.linkUrl ? "Link ready to copy" : "No link created yet",
    connected: false,
    canAct: true,
  };
}

const text = (value: unknown): string | null =>
  typeof value === "string" && value.trim() !== "" ? value : null;

/** Who a link can be emailed to: a person of record with an email. */
export interface AdvanceLinkRecipient {
  id: string;
  name: string;
  email: string;
}

/**
 * The account's people with an email of record, the PRINCIPAL OWNER first when
 * PMS names one who is on the roster. The principal is read from PMS's
 * `principalOwner`, never re-derived from shares; this only finds their row —
 * by `personnelId` (PMS-S840), or by name for a principal with no id yet.
 */
export function advanceLinkRecipients(
  personnel: readonly Personnel[],
  principal: Pick<PrincipalOwner, "name" | "personnelId"> | null,
): AdvanceLinkRecipient[] {
  const rows = personnel.flatMap((p) => {
    const email = text(p.email);
    return email
      ? [{ id: p.id, name: `${p.firstName} ${p.lastName}`.trim(), email }]
      : [];
  });
  const first = rows.findIndex((r) =>
    principal?.personnelId != null
      ? r.id === principal.personnelId
      : r.name === principal?.name,
  );
  return first <= 0
    ? rows
    : [rows[first], ...rows.filter((_, i) => i !== first)];
}

export const ADVANCE_LINK_KINDS = ADVANCE_EVIDENCE_REQUIREMENTS;

/** The two rows' names. "Plaid" is the vendor's own required branding; the
 *  accounting connection is ours (BRK-S322). */
export const ADVANCE_LINK_COPY: Record<
  AdvanceEvidenceRequirement,
  { name: string; sub: string }
> = {
  bank: {
    name: "Bank account",
    sub: "Connect the business bank account through Plaid",
  },
  accounting: {
    name: "Accounting software",
    sub: "Connect the business's accounting software",
  },
};

/**
 * POST /api/broker/accounts/{id}/connections/{kind}/send `{email}` — PMS emails
 * the client their link through its own template and records `sentAt` (D4).
 *
 * The answer (the updated connection row) is not read: the caller re-reads
 * the connections, so what the page shows is PMS's own record. A refusal
 * throws for the caller to render — 422 VALIDATION_FAILED on `email` or
 * RECIPIENT_UNDELIVERABLE, 409 CONNECTION_COMPLETE or CONNECTION_UNAVAILABLE
 * (PMS-S840).
 */
export async function sendAdvanceLink(
  request: AuthedRequester,
  accountId: string,
  kind: AdvanceEvidenceRequirement,
  email: string,
): Promise<void> {
  await request<unknown>(
    `/api/broker/accounts/${encodeURIComponent(accountId)}/connections/${kind}/send`,
    { method: "POST", body: { email } },
  );
}
